import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChartBarIcon,
  EyeIcon,
  UserCircleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  StarIcon,
  AcademicCapIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { adminManagementAPI } from '../services/api';
import { PaginationInfo } from '../types';
import { toast } from 'react-toastify';
import CandidateSkillSummary from '../components/CandidateSkillSummary';
import { formatDate } from '../utils/dateUtils';

interface CandidateOverview {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  field: string;
  experience: number;
  isActive: boolean;
  isAvailableImmediately: boolean;
  availability?: string; // Add availability field
  createdAt: string;
}

interface DetailedCandidate extends CandidateOverview {
  bio?: string;
  skills?: string[];
  location?: string;
  resumeUrl?: string;
  expectedSalary?: number;
  salaryCurrency?: string; // Add missing currency field
  availability?: string; // Add availability field
}

const CandidateManagement: React.FC = () => {
  const [candidates, setCandidates] = useState<CandidateOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<DetailedCandidate | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [showSkillSummary, setShowSkillSummary] = useState(false);
  const [skillSummaryProfileId, setSkillSummaryProfileId] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });

  const fields = ['technology', 'healthcare', 'finance', 'marketing', 'sales', 'education', 
    'consulting', 'manufacturing', 'retail', 'media', 'legal', 'other'];

  useEffect(() => {
    loadCandidates();
  }, [pagination.currentPage, selectedField, availabilityFilter]);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const response = await adminManagementAPI.getCandidates({
        page: pagination.currentPage,
        limit: 10,
        field: selectedField || undefined,
        immediateOnly: availabilityFilter === 'immediate' ? true : undefined,
        search: searchTerm || undefined
      });
      setCandidates(response.data.candidates);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadCandidates();
  };

  const handleViewCandidate = async (candidateId: string) => {
    setViewLoading(true);
    setShowViewModal(true);
    try {
      const response = await adminManagementAPI.getCandidateById(candidateId);
      setSelectedCandidate(response.data);
    } catch (error) {
      toast.error('Failed to load candidate details');
    } finally {
      setViewLoading(false);
    }
  };

  // Add helper function to format availability status for detailed candidate modal
  const getDetailedAvailabilityStatus = (candidate: DetailedCandidate) => {
    // Use the availability field if available, otherwise fall back to isAvailableImmediately
    if (candidate.availability) {
      switch (candidate.availability) {
        case 'immediately':
          return {
            text: 'Available Immediately',
            bgColor: 'bg-green-100 dark:bg-green-900',
            textColor: 'text-green-800 dark:text-green-200'
          };
        case 'within_7_days':
          return {
            text: 'Available in 7 days',
            bgColor: 'bg-blue-100 dark:bg-blue-900',
            textColor: 'text-blue-800 dark:text-blue-200'
          };
        case 'within_15_days':
          return {
            text: 'Available in 15 days',
            bgColor: 'bg-orange-100 dark:bg-orange-900',
            textColor: 'text-orange-800 dark:text-orange-200'
          };
        default:
          return null;
      }
    }
    
    // Legacy support for isAvailableImmediately boolean
    if (candidate.isAvailableImmediately) {
      return {
        text: 'Available Immediately',
        bgColor: 'bg-green-100 dark:bg-green-900',
        textColor: 'text-green-800 dark:text-green-200'
      };
    }
    
    return {
      text: 'Not Immediately Available',
      bgColor: 'bg-red-100 dark:bg-red-900',
      textColor: 'text-red-800 dark:text-red-200'
    };
  };

  // Add helper function to format availability status
  const getAvailabilityStatus = (candidate: CandidateOverview) => {
    // Use the availability field if available, otherwise fall back to isAvailableImmediately
    if (candidate.availability) {
      switch (candidate.availability) {
        case 'immediately':
          return {
            text: 'Available Now',
            bgColor: 'bg-green-100 dark:bg-green-900',
            textColor: 'text-green-800 dark:text-green-200',
            icon: 'clock'
          };
        case 'within_7_days':
          return {
            text: 'Available in 7 days',
            bgColor: 'bg-blue-100 dark:bg-blue-900',
            textColor: 'text-blue-800 dark:text-blue-200',
            icon: 'clock'
          };
        case 'within_15_days':
          return {
            text: 'Available in 15 days',
            bgColor: 'bg-orange-100 dark:bg-orange-900',
            textColor: 'text-orange-800 dark:text-orange-200',
            icon: 'clock'
          };
        default:
          return null;
      }
    }
    
    // Legacy support for isAvailableImmediately boolean
    if (candidate.isAvailableImmediately) {
      return {
        text: 'Available Now',
        bgColor: 'bg-green-100 dark:bg-green-900',
        textColor: 'text-green-800 dark:text-green-200',
        icon: 'clock'
      };
    }
    
    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Candidate Management</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Review and manage candidate profiles and availability status
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card-admin p-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-admin pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <select
              value={selectedField}
              onChange={(e) => setSelectedField(e.target.value)}
              className="input-admin"
            >
              <option value="">All Fields</option>
              {fields.map(field => (
                <option key={field} value={field}>
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </option>
              ))}
            </select>

            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              className="input-admin"
            >
              <option value="">All Availability</option>
              <option value="immediate">Available Immediately</option>
              <option value="not-immediate">Not Immediately Available</option>
            </select>
            
            <button type="submit" className="btn-admin">Search</button>
          </div>
        </form>
      </div>

      {/* Candidates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-600"></div>
          </div>
        ) : candidates.length === 0 ? (
          <div className="col-span-2 text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No candidates found.</p>
          </div>
        ) : (
          candidates.map((candidate) => (
            <div key={candidate.id} className="card-admin p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {candidate.firstName} {candidate.lastName}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {candidate.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      Inactive
                    </span>
                  )}
                  {getAvailabilityStatus(candidate) && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAvailabilityStatus(candidate)?.bgColor} ${getAvailabilityStatus(candidate)?.textColor}`}>
                      <ClockIcon className="w-3 h-3 mr-1" />
                      {getAvailabilityStatus(candidate)?.text}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Field:</span>
                  <span className="ml-2 capitalize">{candidate.field}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Experience:</span>
                  <span className="ml-2">{candidate.experience} years</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Joined:</span>
                  <span className="ml-2">{formatDate(candidate.createdAt)}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-2">
                  <button 
                    className="flex-1 btn-admin text-sm flex items-center justify-center"
                    onClick={() => handleViewCandidate(candidate.id)}
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    View Profile
                  </button>
                  <button 
                    className="flex-1 btn-admin-secondary text-sm flex items-center justify-center"
                    onClick={() => {
                      setSkillSummaryProfileId(candidate.id);
                      setShowSkillSummary(true);
                    }}
                  >
                    <ChartBarIcon className="w-4 h-4 mr-1" />
                    Skills
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && candidates.length > 0 && (
        <div className="card-admin p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={!pagination.hasPrev}
                className="btn-admin-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={!pagination.hasNext}
                className="btn-admin-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Candidate Modal - MODERNIZED */}
      {showViewModal && selectedCandidate && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setShowViewModal(false)}
          ></div>
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
            
            {/* Modern Header with Gradient */}
            <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 px-8 py-8">
              <div className="absolute inset-0 bg-black/10"></div>
              <button 
                className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-200 text-white"
                onClick={() => setShowViewModal(false)}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              
              <div className="relative flex items-start space-x-6">
                {/* Profile Avatar */}
                <div className="relative">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border-2 border-white/30">
                    <UserCircleIcon className="w-16 h-16 text-white" />
                  </div>
                  {selectedCandidate.isActive && (
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-3 border-white">
                      <CheckCircleIcon className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Profile Info */}
                <div className="flex-1 text-white">
                  <h2 className="text-3xl font-bold mb-2">
                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                  </h2>
                  <p className="text-blue-100 text-lg mb-3 font-medium capitalize">
                    {selectedCandidate.field} Professional
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5">
                      <AcademicCapIcon className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">{selectedCandidate.experience} years experience</span>
                    </div>
                    {getDetailedAvailabilityStatus(selectedCandidate) && (
                      <div className={`flex items-center rounded-full px-3 py-1.5 ${
                        selectedCandidate.availability === 'immediately' || selectedCandidate.isAvailableImmediately 
                          ? 'bg-green-500/80 backdrop-blur-sm' 
                          : 'bg-orange-500/80 backdrop-blur-sm'
                      }`}>
                        <FireIcon className="w-4 h-4 mr-2" />
                        <span className="text-sm font-medium">{getDetailedAvailabilityStatus(selectedCandidate)?.text}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Content Area */}
            <div className="overflow-y-auto max-h-[60vh] p-8">
              {viewLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600"></div>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 to-blue-500 opacity-20 blur-sm"></div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg">Loading candidate details...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  
                  {/* Contact & Basic Info Cards */}
                  <div className="grid md:grid-cols-2 gap-6">
                    
                    {/* Contact Information Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mr-3">
                          <UserCircleIcon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16">Email:</span>
                          <span className="text-sm text-gray-900 dark:text-white font-medium">{selectedCandidate.email}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {selectedCandidate.location || 'Location not specified'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CalendarDaysIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            Joined {formatDate(selectedCandidate.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Compensation Card */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-green-100 dark:border-green-800">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center mr-3">
                          <BanknotesIcon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compensation</h3>
                      </div>
                      <div className="text-center">
                        {selectedCandidate.expectedSalary !== undefined ? (
                          <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                              {selectedCandidate.salaryCurrency || 'INR'} {selectedCandidate.expectedSalary.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">Expected Annual Salary</div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-lg text-gray-400 dark:text-gray-500 mb-1">Not Specified</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">Salary expectations</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bio Section */}
                  {selectedCandidate.bio && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-100 dark:border-purple-800">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mr-3">
                          <DocumentTextIcon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">About</h3>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{selectedCandidate.bio}</p>
                    </div>
                  )}

                  {/* Skills Section */}
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-6 border border-orange-100 dark:border-orange-800">
                    <div className="flex items-center mb-6">
                      <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center mr-3">
                        <StarIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Skills & Expertise</h3>
                    </div>
                    
                    {selectedCandidate.skills && selectedCandidate.skills.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedCandidate.skills.map((skill, index) => (
                          <div 
                            key={skill} 
                            className="group relative bg-white dark:bg-gray-800 rounded-xl p-3 border border-orange-200 dark:border-orange-700 hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer"
                          >
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                                <span className="text-white font-bold text-xs">
                                  {skill.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-200">
                                {skill}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                          <StarIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">No skills listed yet</p>
                      </div>
                    )}
                  </div>

                  {/* Resume Section */}
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-indigo-100 dark:border-indigo-800">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center mr-3">
                        <DocumentTextIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resume</h3>
                    </div>
                    
                    {selectedCandidate.resumeUrl ? (
                      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl p-4 border border-indigo-200 dark:border-indigo-700">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mr-4">
                            <DocumentTextIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">Resume Document</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">PDF Format</div>
                          </div>
                        </div>
                        <a 
                          href={selectedCandidate.resumeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                        >
                          <EyeIcon className="w-4 h-4 mr-2" />
                          View Resume
                        </a>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                          <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">No resume uploaded</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modern Footer */}
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button 
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                    onClick={() => {
                      setSkillSummaryProfileId(selectedCandidate.id);
                      setShowSkillSummary(true);
                    }}
                  >
                    <ChartBarIcon className="w-4 h-4 mr-2" />
                    View Skills Analysis
                  </button>
                </div>
                <button 
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors duration-200"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skill Summary Modal */}
      {showSkillSummary && skillSummaryProfileId && (
        <CandidateSkillSummary 
          profileId={skillSummaryProfileId}
          onClose={() => setShowSkillSummary(false)}
        />
      )}
    </div>
  );
};

export default CandidateManagement;